import React from "react";
import TabPanel, { Item as TabPanelItem } from "devextreme-react/tab-panel";
import { SelectBox } from "devextreme-react/select-box";
import TextBox from "devextreme-react/text-box";
import DateBox from "devextreme-react/date-box";
import NumberBox from "devextreme-react/number-box";
import { GetFormatedDateWithTime } from "../../../common/Utils";

// The User Defined tab of an Internal Ownership Transfer header (legacy Codes / Dates / Quantities / References
// sub-tabs): ten fields of each kind, index 0 being field 1.
export const USER_DEFINED_FIELD_COUNT = 10;
// WMTRANSACTIONHEADER.USERDEFINEDREFERENCE_n is char(50) (a code is picked from a list, so its length is the list's).
const REFERENCE_MAX_LENGTH = 50;
// WMCODES.CODETYPE behind User Defined Code 1..10 (legacy WMCodes '03'..'12').
const CODE_TYPES = ['03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];

const fieldNumbers = Array.from({ length: USER_DEFINED_FIELD_COUNT }, (_, index) => index);
const blankList = (blank) => fieldNumbers.map(() => blank);

export const emptyUserDefinedFields = () => ({
    userDefinedCodes: blankList(""),
    userDefinedDates: blankList(null),
    userDefinedQuantities: blankList(null),
    userDefinedReferences: blankList(""),
});

// A profile's user-defined defaults, as the legacy ApplyProfile copied them into the header. Char columns come
// back space-padded, so text is trimmed.
export const userDefinedFieldsFromProfile = (profile) => {
    const text = (value) => (value == null ? "" : String(value).trim());
    const field = (kind, convert) => fieldNumbers.map(index => convert(profile?.[`userdefined${kind}${index + 1}`]));
    return {
        userDefinedCodes: field('code', text),
        userDefinedDates: field('date', value => value || null),
        userDefinedQuantities: field('quantity', value => value ?? null),
        userDefinedReferences: field('reference', text),
    };
};

// The facility's codes filed under the Code field they belong to: index 0 is Code 1.
export const userDefinedCodeOptions = (codes) => CODE_TYPES.map(type => (codes || []).filter(code => code.codeType === type));
export const emptyUserDefinedCodeOptions = () => userDefinedCodeOptions([]);

// The header's user-defined fields in request form (dates as yyyy-MM-ddTHH:mm:ss, like the other dates).
export const userDefinedFieldsForRequest = (header) => ({
    userDefinedCodes: header.userDefinedCodes,
    userDefinedDates: header.userDefinedDates.map(GetFormatedDateWithTime),
    userDefinedQuantities: header.userDefinedQuantities,
    userDefinedReferences: header.userDefinedReferences,
});

const codeLabel = (code) => (code ? (code.description ? `${code.code} - ${code.description}` : code.code) : "");

// Four sub-tabs of ten fields each. `header` is the Shipment or Receipt state, `codeOptions` the per-field code
// lists (see userDefinedCodeOptions), `onChange(kind, index, value)` reports an edit.
const UserDefinedFieldsTab = ({ header, codeOptions, disabled, onChange }) => {
    const rows = (renderField) => (
        <div className="row pt-3">
            {fieldNumbers.map(index => (
                <div className="col-md-3 mb-3" key={index}>
                    {renderField(index)}
                </div>
            ))}
        </div>
    );
    return (
        <TabPanel stylingMode={'secondary'} className="TabpanelContainer">
            <TabPanelItem title="Codes">
                {rows(index => (
                    <SelectBox
                        dataSource={codeOptions?.[index] || []}
                        searchEnabled={true}
                        showClearButton={true}
                        placeholder={`Enter Code ${index + 1}`}
                        valueExpr="code"
                        displayExpr={codeLabel}
                        searchExpr={['code', 'description']}
                        value={header.userDefinedCodes[index] || null}
                        onValueChanged={(e) => onChange('userDefinedCodes', index, e.value || "")}
                        disabled={disabled}
                        stylingMode='outlined'
                        labelMode="static"
                        label={`Code ${index + 1}:`}
                    />
                ))}
            </TabPanelItem>
            <TabPanelItem title="Dates">
                {rows(index => (
                    <DateBox
                        type="date"
                        min={new Date(1900, 0, 1)}
                        max={new Date(2099, 11, 31)}
                        showClearButton={true}
                        value={header.userDefinedDates[index]}
                        onValueChanged={(e) => onChange('userDefinedDates', index, e.value || null)}
                        disabled={disabled}
                        stylingMode='outlined'
                        labelMode="static"
                        label={`Date ${index + 1}:`}
                    />
                ))}
            </TabPanelItem>
            <TabPanelItem title="Quantities">
                {rows(index => (
                    <NumberBox
                        showSpinButtons={false}
                        value={header.userDefinedQuantities[index]}
                        onValueChanged={(e) => onChange('userDefinedQuantities', index, e.value ?? null)}
                        disabled={disabled}
                        stylingMode='outlined'
                        labelMode="static"
                        label={`Quantity ${index + 1}:`}
                    />
                ))}
            </TabPanelItem>
            <TabPanelItem title="References">
                {rows(index => (
                    <TextBox
                        placeholder={`Enter Reference ${index + 1}`}
                        maxLength={REFERENCE_MAX_LENGTH}
                        value={header.userDefinedReferences[index]}
                        onValueChanged={(e) => onChange('userDefinedReferences', index, e.value || "")}
                        disabled={disabled}
                        stylingMode='outlined'
                        labelMode="static"
                        label={`Reference ${index + 1}:`}
                    />
                ))}
            </TabPanelItem>
        </TabPanel>
    );
};

export default UserDefinedFieldsTab;
